<script setup lang="ts">
/**
 * The terms block of a real-money confirm (ConfirmHost, `checkout: true`):
 * the consent checkbox that gates Pay, and the accepted payment methods.
 * It owns no state — the tick is the host's v-model, and opening a document is
 * an event, because the viewer must render outside the dialog's backdrop
 * (its `backdrop-filter` would trap a fixed-position panel inside the card).
 */
import { computed } from 'vue';
import {
  CONSENT_DOCS, consentParts, type CheckoutTerms, type ConsentDoc,
} from '../game/checkout-terms';
import { FOUNDING_PAPERS, legalDocTitle } from '../game/legal-api';
import { t } from '../i18n';

const props = defineProps<{ modelValue: boolean; terms: CheckoutTerms | null }>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'open-doc', slug: string): void;
}>();

const parts = computed(() =>
  consentParts(t('shop.consent.agree', { terms: '{terms}', privacy: '{privacy}' })));

/** The CMS title when the document is published, else its translated seed title. */
function docLabel(doc: ConsentDoc): string {
  const slug = CONSENT_DOCS[doc];
  const live = props.terms?.docs.find((d) => d.slug === slug);
  if (live) return legalDocTitle(live);
  const paper = FOUNDING_PAPERS.find((p) => p.slug === slug);
  return paper ? t(paper.key) : slug;
}

const methods = computed(() => props.terms?.methods ?? []);
</script>

<template>
  <div class="ct">
    <label class="ct-consent">
      <input
        type="checkbox"
        class="ct-box"
        :checked="modelValue"
        @change="emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
      />
      <span>
        <template v-for="(p, i) in parts" :key="i">
          <button
            v-if="'doc' in p"
            type="button"
            class="ct-link"
            @click="emit('open-doc', CONSENT_DOCS[p.doc])"
          >{{ docLabel(p.doc) }}</button>
          <template v-else>{{ p.text }}</template>
        </template>
      </span>
    </label>

    <div v-if="methods.length" class="ct-methods">
      <span>{{ t('shop.consent.methods') }}</span>
      <img
        v-for="m in methods"
        :key="m.slug"
        :src="`/payment-methods/${m.icon}.svg`"
        :alt="m.name"
        :title="m.name"
      />
    </div>
  </div>
</template>

<style scoped>
.ct { margin-top: 12px; text-align: left; }

/* the whole line is the tap target (44px floor), not just the 22px box */
.ct-consent {
  display: flex; align-items: center; gap: 12px;
  min-height: 44px; padding: 4px 2px;
  font-family: var(--font-body, serif);
  font-size: 13px; line-height: 1.4;
  color: #c8d4e2;
  cursor: pointer;
}

.ct-box {
  appearance: none;
  flex: none;
  width: 22px; height: 22px; margin: 0;
  border: 1px solid color-mix(in srgb, var(--color-gold) 70%, transparent);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.35);
  display: grid; place-content: center;
  cursor: pointer;
}
.ct-box::before {
  content: '';
  width: 11px; height: 6px;
  border: solid var(--color-gold-bright, var(--color-gold));
  border-width: 0 0 2px 2px;
  transform: translateY(-2px) rotate(-45deg) scale(0);
  transition: transform 120ms ease;
}
.ct-box:checked::before { transform: translateY(-2px) rotate(-45deg) scale(1); }
.ct-box:focus-visible { outline: 2px solid var(--color-gold); outline-offset: 2px; }

.ct-link {
  display: inline; padding: 0; border: 0; background: none;
  font: inherit; color: var(--color-gold);
  cursor: pointer;
}
.ct-link:hover, .ct-link:focus-visible { text-decoration: underline; }

.ct-methods {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px 10px;
  margin-top: 6px; padding-inline: 2px;
  font-family: var(--font-body, serif);
  font-size: 13px; color: #c8d4e2;
}
/* the method art is card-shaped (38x24 viewBox) — keep its aspect */
.ct-methods img { height: 24px; width: auto; }

@media (prefers-reduced-motion: reduce) {
  .ct-box::before { transition: none; }
}
</style>
